import { useEffect, useState, type FormEvent } from "react";
import { ApiError, apiRequest } from "../api";
import { CheckIcon, EyeIcon, PinIcon } from "../components/Icons";

interface LoginResult {
  authenticated: true;
  mustChangePassword: boolean;
  csrfToken: string;
}

interface ChangePasswordResult extends LoginResult {
  mustChangePassword: false;
  readToken: string;
  importLinks: { profileUrl: string; moduleUrl: string };
}

export function LoginPage({ onSuccess }: { onSuccess: (result: LoginResult) => void }) {
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lockSeconds, setLockSeconds] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    if (lockSeconds <= 0) return;
    const timer = window.setInterval(() => setLockSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [lockSeconds]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || lockSeconds > 0) return;
    setBusy(true);
    setError("");
    try {
      onSuccess(
        await apiRequest<LoginResult>("/api/auth/login", {
          method: "POST",
          body: { password }
        })
      );
    } catch (caught) {
      const apiError = caught instanceof ApiError ? caught : null;
      setError(apiError?.message ?? "密码错误或暂时无法登录");
      if (apiError?.retryAfterSeconds) setLockSeconds(apiError.retryAfterSeconds);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={submit} aria-busy={busy}>
        <div className="auth-brand">
          <PinIcon className="auth-logo" />
          <h1>iOSIP</h1>
        </div>
        <div className="field-stack">
          <label htmlFor="admin-password">管理员密码</label>
          <div className="password-field">
            <input
              id="admin-password"
              type={visible ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
              required
              disabled={busy || lockSeconds > 0}
              aria-describedby={error ? "login-error" : undefined}
            />
            <button
              type="button"
              className="icon-button input-action"
              onClick={() => setVisible((value) => !value)}
              aria-label={visible ? "隐藏密码" : "显示密码"}
            >
              <EyeIcon open={!visible} />
            </button>
          </div>
        </div>
        <button
          className="primary-button auth-submit"
          type="submit"
          disabled={busy || lockSeconds > 0 || !online}
        >
          {busy ? "登录中…" : lockSeconds > 0 ? `${lockSeconds} 秒后重试` : "登录"}
        </button>
        <div
          id="login-error"
          className="form-message error-message"
          role="alert"
          aria-live="polite"
        >
          {!online ? "当前离线，请恢复网络后重试" : error}
        </div>
      </form>
    </main>
  );
}

export function ChangePasswordPage({
  csrfToken,
  onSuccess
}: {
  csrfToken: string;
  onSuccess: (result: ChangePasswordResult) => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const longEnough = password.length >= 12;
  const matches = password.length > 0 && password === confirmation;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!longEnough || !matches || busy) return;
    setBusy(true);
    setError("");
    try {
      onSuccess(
        await apiRequest<ChangePasswordResult>("/api/auth/change-password", {
          method: "POST",
          csrfToken,
          body: { newPassword: password, confirmation }
        })
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法保存新密码");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <form className="auth-card change-password-card" onSubmit={submit} aria-busy={busy}>
        <div className="auth-brand compact-brand">
          <PinIcon className="auth-logo" />
          <span>iOSIP</span>
        </div>
        <header className="auth-copy">
          <h1>设置新密码</h1>
          <p>初始密码仅用于首次启动，请立即设置新的管理员密码。</p>
        </header>
        <div className="field-stack">
          <label htmlFor="new-password">新密码</label>
          <div className="password-field">
            <input
              id="new-password"
              type={visible ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              autoFocus
              required
              minLength={12}
            />
            <button
              type="button"
              className="icon-button input-action"
              onClick={() => setVisible((value) => !value)}
              aria-label={visible ? "隐藏密码" : "显示密码"}
            >
              <EyeIcon open={!visible} />
            </button>
          </div>
        </div>
        <div className="field-stack">
          <label htmlFor="confirm-password">确认新密码</label>
          <input
            id="confirm-password"
            type={visible ? "text" : "password"}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <ul className="password-checks" aria-live="polite">
          <li className={longEnough ? "valid" : ""}>
            <CheckIcon /> 至少 12 个字符
          </li>
          <li className={matches ? "valid" : ""}>
            <CheckIcon /> 两次输入一致
          </li>
        </ul>
        <button
          className="primary-button auth-submit"
          type="submit"
          disabled={!longEnough || !matches || busy}
        >
          {busy ? "保存中…" : "保存新密码"}
        </button>
        <div className="form-message error-message" role="alert" aria-live="polite">
          {error}
        </div>
      </form>
    </main>
  );
}
